import Experience from '../Experience.js'
import { exportDeskPdf } from '../Utils/PdfExporter.js'

const formatMillimeters = (value) => `${Math.round(value)} mm`

const formatInches = (value) => `${(value / 25.4).toFixed(1)} in`

const formatPrice = (value) =>
{
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
    }).format(value)
}

export default class WorkTableConfigurator
{
    constructor(workTable)
    {
        this.experience = new Experience()
        this.workTable = workTable
        this.sliderRows = {}
        this.priceValue = null
        this.priceSubtitle = null
        this.dimensionToggle = null
        this.upperShelfToggle = null
        this.storageUnitToggle = null
        this.exportButton = null
        this.finishButtons = {}
        this.frameButtons = {}

        this.setPanel()
        this.unsubscribe = this.workTable.subscribe((state) =>
        {
            this.sync(state)
        })
        this.sync(this.workTable.getState())
    }

    setPanel()
    {
        this.element = document.createElement('aside')
        this.element.className = 'desk-configurator'

        const priceCard = document.createElement('div')
        priceCard.className = 'desk-configurator__price'

        const eyebrow = document.createElement('p')
        eyebrow.className = 'desk-configurator__eyebrow'
        eyebrow.textContent = 'Estimated Work Desk Price'

        this.priceValue = document.createElement('h2')
        this.priceValue.className = 'desk-configurator__price-value'

        this.priceSubtitle = document.createElement('p')
        this.priceSubtitle.className = 'desk-configurator__price-subtitle'

        priceCard.append(eyebrow, this.priceValue, this.priceSubtitle)
        this.priceCard = priceCard
        document.body.append(priceCard)

        const header = document.createElement('div')
        header.className = 'desk-configurator__header'

        const title = document.createElement('h1')
        title.className = 'desk-configurator__title'
        title.textContent = 'Work Desk'

        this.exportButton = document.createElement('button')
        this.exportButton.className = 'desk-configurator__export'
        this.exportButton.type = 'button'
        this.exportButton.textContent = 'Export PDF'
        this.exportButton.addEventListener('click', async () =>
        {
            await this.handleExportPdf()
        })

        header.append(title, this.exportButton)

        const toggleRow = document.createElement('label')
        toggleRow.className = 'desk-configurator__toggle'

        const toggleCopy = document.createElement('div')

        const toggleTitle = document.createElement('span')
        toggleTitle.className = 'desk-configurator__toggle-title'
        toggleTitle.textContent = 'Show dimensions'

        const toggleHint = document.createElement('span')
        toggleHint.className = 'desk-configurator__toggle-hint'
        toggleHint.textContent = 'Turn the 3D measurement arrows on or off'

        toggleCopy.append(toggleTitle, toggleHint)

        this.dimensionToggle = document.createElement('input')
        this.dimensionToggle.className = 'desk-configurator__toggle-input'
        this.dimensionToggle.type = 'checkbox'
        this.dimensionToggle.addEventListener('change', (event) =>
        {
            this.workTable.setShowDimensions(event.target.checked)
        })

        toggleRow.append(toggleCopy, this.dimensionToggle)

        const shelfToggleRow = document.createElement('label')
        shelfToggleRow.className = 'desk-configurator__toggle'

        const shelfCopy = document.createElement('div')

        const shelfTitle = document.createElement('span')
        shelfTitle.className = 'desk-configurator__toggle-title'
        shelfTitle.textContent = 'Upper shelf'

        const shelfHint = document.createElement('span')
        shelfHint.className = 'desk-configurator__toggle-hint'
        shelfHint.textContent = 'Add or remove the raised monitor shelf from the desk.'

        shelfCopy.append(shelfTitle, shelfHint)

        this.upperShelfToggle = document.createElement('input')
        this.upperShelfToggle.className = 'desk-configurator__toggle-input'
        this.upperShelfToggle.type = 'checkbox'
        this.upperShelfToggle.addEventListener('change', (event) =>
        {
            this.workTable.setHasUpperShelf(event.target.checked)
        })

        shelfToggleRow.append(shelfCopy, this.upperShelfToggle)

        const storageToggleRow = document.createElement('label')
        storageToggleRow.className = 'desk-configurator__toggle'

        const storageCopy = document.createElement('div')

        const storageTitle = document.createElement('span')
        storageTitle.className = 'desk-configurator__toggle-title'
        storageTitle.textContent = 'Storage unit'

        const storageHint = document.createElement('span')
        storageHint.className = 'desk-configurator__toggle-hint'
        storageHint.textContent = 'Add or remove the side storage cabinet from the desk.'

        storageCopy.append(storageTitle, storageHint)

        this.storageUnitToggle = document.createElement('input')
        this.storageUnitToggle.className = 'desk-configurator__toggle-input'
        this.storageUnitToggle.type = 'checkbox'
        this.storageUnitToggle.addEventListener('change', (event) =>
        {
            this.workTable.setHasStorageUnit(event.target.checked)
        })

        storageToggleRow.append(storageCopy, this.storageUnitToggle)

        const finishes = document.createElement('section')
        finishes.className = 'desk-configurator__finishes'

        const finishesTitle = document.createElement('h2')
        finishesTitle.className = 'desk-configurator__section-title'
        finishesTitle.textContent = 'Tabletop Finish'

        const finishesHint = document.createElement('p')
        finishesHint.className = 'desk-configurator__section-copy'
        finishesHint.textContent = 'Click a wood sample to preview that plank material on the desktop surface.'

        const finishGrid = document.createElement('div')
        finishGrid.className = 'desk-configurator__finish-grid'

        for(const option of this.workTable.getState().topFinishOptions)
        {
            const button = document.createElement('button')
            button.className = 'desk-configurator__finish'
            button.type = 'button'
            button.addEventListener('click', () =>
            {
                this.workTable.setTopFinish(option.id)
            })

            const thumbnail = document.createElement('img')
            thumbnail.className = 'desk-configurator__finish-thumb'
            thumbnail.src = option.thumbnailUrl
            thumbnail.alt = `${option.label} wood finish`

            const label = document.createElement('span')
            label.className = 'desk-configurator__finish-label'
            label.textContent = option.label

            button.append(thumbnail, label)
            finishGrid.append(button)
            this.finishButtons[option.id] = button
        }

        finishes.append(finishesTitle, finishesHint, finishGrid)

        const frameSection = document.createElement('section')
        frameSection.className = 'desk-configurator__finishes'

        const frameTitle = document.createElement('h2')
        frameTitle.className = 'desk-configurator__section-title'
        frameTitle.textContent = 'Frame'

        const frameHint = document.createElement('p')
        frameHint.className = 'desk-configurator__section-copy'
        frameHint.textContent = 'Change the frame finish for the legs, structural beams, and drawer outer shell.'

        const frameGrid = document.createElement('div')
        frameGrid.className = 'desk-configurator__frame-grid'

        for(const option of this.workTable.getState().frameFinishOptions)
        {
            const button = document.createElement('button')
            button.className = 'desk-configurator__frame-swatch'
            button.type = 'button'
            button.addEventListener('click', () =>
            {
                this.workTable.setFrameFinish(option.id)
            })

            const colorDot = document.createElement('span')
            colorDot.className = 'desk-configurator__frame-dot'
            colorDot.style.setProperty('--frame-color', option.color)
            colorDot.setAttribute('aria-hidden', 'true')

            const label = document.createElement('span')
            label.className = 'desk-configurator__frame-label'
            label.textContent = option.label

            button.append(colorDot, label)
            frameGrid.append(button)
            this.frameButtons[option.id] = button
        }

        frameSection.append(frameTitle, frameHint, frameGrid)

        const controls = document.createElement('div')
        controls.className = 'desk-configurator__controls'

        for(const [key, config] of Object.entries(this.workTable.controls))
        {
            const row = document.createElement('label')
            row.className = 'desk-configurator__control'

            const topLine = document.createElement('div')
            topLine.className = 'desk-configurator__control-top'

            const name = document.createElement('span')
            name.className = 'desk-configurator__control-label'
            name.textContent = config.label

            const value = document.createElement('span')
            value.className = 'desk-configurator__control-value'

            topLine.append(name, value)

            const input = document.createElement('input')
            input.className = 'desk-configurator__slider'
            input.type = 'range'
            input.min = String(config.min)
            input.max = String(config.max)
            input.step = String(config.step)
            input.addEventListener('input', (event) =>
            {
                this.workTable.updateDimensionsMm({
                    [key]: Number(event.target.value)
                })
            })

            row.append(topLine, input)
            controls.append(row)

            this.sliderRows[key] = { input, value }
        }

        this.element.append(header, controls, finishes, frameSection, toggleRow, shelfToggleRow, storageToggleRow)
        document.body.append(this.element)
    }

    sync(state)
    {
        this.priceValue.textContent = formatPrice(state.price)
        this.priceSubtitle.textContent = 'Estimate based on current desk configuration'
        this.dimensionToggle.checked = state.showDimensions
        this.upperShelfToggle.checked = state.hasUpperShelf
        this.storageUnitToggle.checked = state.hasStorageUnit

        for(const [finishId, button] of Object.entries(this.finishButtons))
        {
            button.classList.toggle('is-active', finishId === state.topFinish)
        }

        for(const [finishId, button] of Object.entries(this.frameButtons))
        {
            button.classList.toggle('is-active', finishId === state.frameFinish)
        }

        for(const [key, row] of Object.entries(this.sliderRows))
        {
            const dimension = state.dimensionsMm[key]
            row.input.value = String(dimension)
            row.value.textContent = `${formatMillimeters(dimension)} / ${formatInches(dimension)}`
        }
    }

    async handleExportPdf()
    {
        this.exportButton.disabled = true
        this.exportButton.textContent = 'Exporting...'

        try
        {
            this.experience.renderer.instance.render(this.experience.scene, this.experience.camera.instance)

            const heroImageDataUrl = this.experience.renderer.instance.domElement.toDataURL('image/jpeg', 0.92)
            exportDeskPdf({
                exportData: this.workTable.getExportData(),
                heroImageDataUrl
            })
        }
        finally
        {
            this.exportButton.disabled = false
            this.exportButton.textContent = 'Export PDF'
        }
    }
}
